// Device location captured on map load (deferred until the intro panels are
// closed — see requestUserLocation), reused for "center on me" (RoadRatings
// logo click). Best-effort: denial / unsupported browsers are silently
// ignored — the map keeps its default Bay Area center. The last known fix is
// still cached so the map can open centered instantly while the fresh fix
// resolves; a permission denial is remembered so the browser prompt is NOT
// re-fired on every page load.
//
// Mobile quirk: iOS Safari and several Android browsers require a user
// gesture before geolocation may run, and silently reject a gestureless
// on-load call as PERMISSION_DENIED without ever showing a prompt. So the
// on-load attempt is retried once on the first tap, and a "denial" is only
// remembered when the Permissions API confirms the user really said no.

import type { Map as MLMap } from 'maplibre-gl'

// Zoom shared by the initial Bay Area view, the initial user-location
// centering and the logo-click recenter, so all three feel identical.
export const INITIAL_ZOOM = 9

// Arrived via a shared road/route deep-link (/road/[id], /route/[id]). The
// link's own selection frames the map, so intro panels are skipped and the
// device location must never move the view — even if the user grants it.
export const deepLinkArrival =
  typeof window !== 'undefined' && /^\/(road|route)\//.test(window.location.pathname)

// Live handle to the MapLibre map so UI (logo click) can recenter without
// prop-drilling through the map component.
export const mapInstance: { current: MLMap | null } = { current: null }

export interface UserLocation {
  lat: number
  lon: number
  ts: number
}

const LS_KEY = 'rr_user_location'
// Remembers a permission denial so the browser prompt isn't re-fired on
// every load — the user said no, we don't ask again (until they clear it).
const DENIED_KEY = 'rr_location_denied'
// A stored fix younger than this is considered valid and reused as-is; older
// ones are only a starting point while a fresh fix is requested.
const FRESH_MS = 60 * 60 * 1000

let cached: UserLocation | null = null
const listeners = new Set<(loc: UserLocation) => void>()

export function getStoredUserLocation(): UserLocation | null {
  return cached
}

// Reads the stored fix (if any) into the memory cache WITHOUT triggering a
// geolocation request — lets the map open centered for returning users
// before the deferred request fires (intro panels not shown, or still open).
export function hydrateStoredUserLocation(): void {
  if (cached) return
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as UserLocation
      if (Number.isFinite(parsed?.lat) && Number.isFinite(parsed?.lon)) {
        cached = parsed
      }
    }
  } catch {
    /* ignore corrupt cache */
  }
}

export function onUserLocation(cb: (loc: UserLocation) => void): () => void {
  listeners.add(cb)
  if (cached) cb(cached)
  return () => listeners.delete(cb)
}

// Auto-centering: fly the map to the device location the first time it
// resolves (or is found cached). Only until the user pans/zooms away — after
// that the map stays where they left it. Bay Area default remains the
// fallback when location is denied/unsupported/slow.
let autoCentered = false
let userMoved = false

// Called by the map when the USER (not code) pans/zooms — auto-centering is
// then cancelled so a late location fix never yanks the view away.
export function noteUserMapInteraction(): void {
  userMoved = true
}

export function autoCenterOnUserLocation(): void {
  if (autoCentered || userMoved || deepLinkArrival) return
  const loc = getStoredUserLocation()
  const map = mapInstance.current
  if (!loc || !map) return
  try {
    map.jumpTo({ center: [loc.lon, loc.lat], zoom: INITIAL_ZOOM, bearing: map.getBearing() })
    autoCentered = true
  } catch {
    /* style not ready yet — the map 'load' hook retries */
  }
}

function deniedFlag(): boolean {
  try {
    return localStorage.getItem(DENIED_KEY) === '1'
  } catch {
    return false
  }
}

function setDeniedFlag(): void {
  try {
    localStorage.setItem(DENIED_KEY, '1')
  } catch {
    /* ignore */
  }
}

// Real permission state, or null when the browser doesn't expose it.
async function permissionState(): Promise<PermissionState | null> {
  try {
    if (!navigator.permissions) return null
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return null
  }
}

// One shared request body for the on-load attempt and the first-tap retry.
function requestFix(): void {
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const loc: UserLocation = {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        ts: Date.now(),
      }
      cached = loc
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(loc))
      } catch {
        /* private mode — memory cache still works */
      }
      // Snap the map to the fresh position too — but only if the user hasn't
      // already started panning around on their own.
      autoCenterOnUserLocation()
      listeners.forEach((cb) => cb(loc))
    },
    (err) => {
      if (err?.code !== err.PERMISSION_DENIED) return
      // A gestureless attempt can be rejected without a prompt ever showing
      // (mobile gesture requirement). Only record a real denial when the
      // Permissions API confirms the user actually said no.
      permissionState().then((state) => {
        if (state === 'denied') setDeniedFlag()
      })
    },
    { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 }
  )
}

// Mobile browsers that require a user gesture for geolocation silently
// reject the gestureless on-load attempt — retry once on the first tap,
// which satisfies the gesture requirement and triggers the prompt.
let gestureRetryBound = false
function ensureGestureRetry(): void {
  if (gestureRetryBound || !('geolocation' in navigator)) return
  gestureRetryBound = true
  document.addEventListener(
    'pointerdown',
    () => {
      if (cached && Date.now() - cached.ts < FRESH_MS) return
      if (deniedFlag()) return
      requestFix()
    },
    { once: true, capture: true }
  )
}

// Called by Onboarding: either when the intro panels close (skip / finish —
// that tap itself is a user gesture, so mobile browsers show the prompt right
// away), or on page load when no intro is presented. Until then the stored
// fix alone (hydrateStoredUserLocation) shapes the initial map center.
export function requestUserLocation(): boolean {
  // Deep-link arrivals never prompt or auto-center — the shared road/route
  // link is the content, and centering on the visitor would defeat it.
  if (deepLinkArrival) return false
  // 1) Any previously stored fix is used immediately so the map opens where
  //    the user last was (no blank wait on the GPS).
  hydrateStoredUserLocation()
  if (cached) autoCenterOnUserLocation()

  if (!('geolocation' in navigator)) return !!cached

  // The user already said no — respect that instead of re-prompting.
  if (deniedFlag()) return !!cached

  // 2) A fresh fix is requested unless the stored one is younger than
  //    FRESH_MS — the stored one can be days old and the rider may have
  //    traveled. Browsers don't re-prompt once permission was granted (the
  //    answer comes back instantly), and a stale stored fix is simply
  //    replaced below.
  if (cached && Date.now() - cached.ts < FRESH_MS) return !!cached
  // Gestureless attempts can be silently rejected on mobile — arm a
  // first-tap retry so the prompt still appears there.
  ensureGestureRetry()
  requestFix()
  return !!cached
}
